import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { IconComponent } from '@shared/components/ui/icon';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary' | 'contrast' | 'help';
export type BadgeSize = 'sm' | 'md' | 'lg' | 'small';
export type BadgeStyle = 'filled' | 'light' | 'outlined' | Record<string, string>;

/**
 * GAP-FE-021B · migrated from decorator @Input to signal input().
 *
 * Legacy aliases handled via computed signals:
 *  - severity → effectiveVariant (legacy severity strings mapped to BadgeVariant)
 *  - 'small' size → effectiveSize ('sm')
 *  - label / value → displayText (uses the first non-empty)
 */
@Component({
  selector: 'ui-badge',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './badge.component.html',
  styleUrls: ['./badge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BadgeComponent {
  readonly variant = input<BadgeVariant>('default');
  readonly size = input<BadgeSize>('md');
  readonly style = input<BadgeStyle>('light');
  readonly removable = input<boolean>(false);
  readonly dot = input<boolean>(false);
  readonly tooltipLabel = input<string | undefined>(undefined);

  // Tag-style compatibility inputs (label/value/icon mirror the upstream
  // tag-component API consumed by some legacy templates).
  readonly label = input<string | number | undefined>(undefined);
  readonly value = input<string | number | undefined>(undefined);
  readonly icon = input<string | undefined>(undefined);
  readonly rounded = input<boolean>(false);
  readonly styleClass = input<string | undefined>(undefined);
  readonly severity = input<string | undefined>(undefined);

  readonly effectiveVariant = computed<BadgeVariant>(() => {
    const sev = this.severity();
    if (sev !== undefined) {
      const severityMap: Record<string, BadgeVariant> = {
        'success': 'success',
        'info': 'info',
        'warning': 'warning',
        'danger': 'danger',
        'secondary': 'default',
        'contrast': 'primary'
      };
      return severityMap[sev] || 'default';
    }
    return this.variant();
  });

  readonly effectiveSize = computed<BadgeSize>(() => {
    const s = this.size();
    return s === 'small' ? 'sm' : s;
  });

  readonly displayText = computed<string>(() => {
    const text = this.label() ?? this.value() ?? '';
    return String(text);
  });

  readonly badgeClasses = computed<string>(() => {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'gap-1.5',
      'rounded-full',
      'font-medium',
      'transition-colors'
    ];

    const sizeClasses: Record<BadgeSize, string[]> = {
      sm: ['px-2', 'py-0.5', 'text-xs'],
      small: ['px-2', 'py-0.5', 'text-xs'],
      md: ['px-2.5', 'py-0.5', 'text-xs'],
      lg: ['px-3', 'py-1', 'text-sm']
    };
    baseClasses.push(...sizeClasses[this.effectiveSize()]);

    const s = this.style();
    const v = this.effectiveVariant();

    if (s === 'filled') {
      const filledClasses: Record<BadgeVariant, string[]> = {
        default: ['bg-bb-bg-tertiary', 'text-bb-text-primary'],
        primary: ['bg-bb-accent-blue', 'text-bb-text-primary'],
        secondary: ['bg-bb-bg-tertiary', 'text-bb-text-secondary'],
        success: ['bg-bb-accent-green', 'text-bb-text-primary'],
        warning: ['bg-bb-accent-amber', 'text-bb-text-primary'],
        danger: ['bg-bb-accent-red', 'text-bb-text-primary'],
        info: ['bg-bb-accent-blue', 'text-bb-text-primary'],
        contrast: ['bg-bb-text-primary', 'text-bb-bg-primary'],
        help: ['bg-bb-accent-purple', 'text-bb-text-primary']
      };
      baseClasses.push(...filledClasses[v]);
    } else if (s === 'outlined') {
      const outlinedClasses: Record<BadgeVariant, string[]> = {
        default: ['border', 'border-bb-border', 'text-bb-text-secondary', 'bg-transparent'],
        primary: ['border', 'border-bb-accent-blue', 'text-bb-accent-blue', 'bg-transparent'],
        secondary: ['border', 'border-bb-border', 'text-bb-text-secondary', 'bg-transparent'],
        success: ['border', 'border-bb-accent-green', 'text-bb-accent-green', 'bg-transparent'],
        warning: ['border', 'border-bb-accent-amber', 'text-bb-accent-amber', 'bg-transparent'],
        danger: ['border', 'border-bb-accent-red', 'text-bb-accent-red', 'bg-transparent'],
        info: ['border', 'border-bb-accent-blue', 'text-bb-accent-blue', 'bg-transparent'],
        contrast: ['border', 'border-bb-text-primary', 'text-bb-text-primary', 'bg-transparent'],
        help: ['border', 'border-bb-accent-purple', 'text-bb-accent-purple', 'bg-transparent']
      };
      baseClasses.push(...outlinedClasses[v]);
    } else {
      const lightClasses: Record<BadgeVariant, string[]> = {
        default: ['bg-bb-bg-tertiary', 'text-bb-text-secondary'],
        primary: ['bg-bb-accent-blue/15', 'text-bb-accent-blue'],
        secondary: ['bg-bb-bg-tertiary', 'text-bb-text-secondary'],
        success: ['bg-bb-accent-green/15', 'text-bb-accent-green'],
        warning: ['bg-bb-accent-amber/15', 'text-bb-accent-amber'],
        danger: ['bg-bb-accent-red/15', 'text-bb-accent-red'],
        info: ['bg-bb-accent-blue/15', 'text-bb-accent-blue'],
        contrast: ['bg-bb-text-primary/15', 'text-bb-text-primary'],
        help: ['bg-bb-accent-purple/15', 'text-bb-accent-purple']
      };
      baseClasses.push(...lightClasses[v]);
    }

    return baseClasses.join(' ');
  });

  readonly dotClasses = computed<string>(() => {
    const baseClasses = ['w-1.5', 'h-1.5', 'rounded-full'];
    const isFilled = this.style() === 'filled';

    const dotColors: Record<BadgeVariant, string> = {
      default: 'bg-bb-text-secondary',
      primary: isFilled ? 'bg-bb-text-primary' : 'bg-bb-accent-blue',
      secondary: 'bg-bb-text-secondary',
      success: isFilled ? 'bg-bb-text-primary' : 'bg-bb-accent-green',
      warning: isFilled ? 'bg-bb-text-primary' : 'bg-bb-accent-amber',
      danger: isFilled ? 'bg-bb-text-primary' : 'bg-bb-accent-red',
      info: isFilled ? 'bg-bb-text-primary' : 'bg-bb-accent-blue',
      contrast: isFilled ? 'bg-bb-bg-primary' : 'bg-bb-text-primary',
      help: isFilled ? 'bg-bb-text-primary' : 'bg-bb-accent-purple'
    };

    baseClasses.push(dotColors[this.effectiveVariant()]);
    return baseClasses.join(' ');
  });

  readonly removeButtonClasses = 'ml-1 -mr-1 p-0.5 rounded-full hover:bg-black/10 transition-colors cursor-pointer';
}
